import { ExclamationCircleOutlined } from "@ant-design/icons"
import { RightEnum, Vendor } from "@finway-group/shared/lib/models"
import { Button, Tooltip } from "antd"
import { t } from "i18next"
import React from "react"
import { Edit as EditIcon } from "react-feather"

import { useCompany } from "Shared/hooks/company.hooks"
import { useCostCenter2ById, useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useCostCenterById } from "Shared/hooks/costCenter.hooks"
import { useExpenseAccountById, useExpenseAccounts } from "Shared/hooks/expenseAccount.hooks"
import { AuthzService } from "Shared/services"
import { isPaymentFlowInfoDisabled } from "Shared/utils/expense.utils"
import { getTooltipPopupContainer } from "Shared/utils/helper.utils"

interface VendorDetailRulesInterface {
    vendor: Vendor
    openRuleModal: () => void
}

export const VendorDetailRules = ({ vendor, openRuleModal }: VendorDetailRulesInterface) => {
    const costCenter = useCostCenterById(vendor.rule?.costCenter, true)
    const costCenter2 = useCostCenter2ById(vendor.rule?.costCenter2)
    const costCenters2 = useCostCenters2()
    const expenseAccount = useExpenseAccountById(vendor.rule?.expenseAccount)
    const expenseAccounts = useExpenseAccounts()
    const company = useCompany()
    const discountRule = vendor.rule?.discounts?.[0] // In anticipation of multiple discounts

    const renderEditRulesIcon = () => (vendor.deleted ? <></> : <EditIcon className="ml-14 cursor-pointer w-14 min-w-14" onClick={openRuleModal} />)

    const renderVendorRuleButton = () =>
        vendor.deleted ? (
            <></>
        ) : (
            <Button size="small" className="btn-default block w-full" onClick={openRuleModal}>
                <span>{t("action:set")}</span>
            </Button>
        )

    const renderDiscountLabel = () => (!discountRule ? "-" : `${discountRule.percentage}% - ${t("label:vendor_discount.days", { count: discountRule.period })}`)

    return (
        <>
            <ul className="description-list overflow-hidden">
                <li>
                    <span className="title min-w-100">{t("label:cost_center")}</span>
                    <div className="flex text truncate min-w-100 items-center justify-end">
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && vendor.isApproved ? (
                            costCenter ? (
                                <>
                                    <span className="flex overflow-x-auto">
                                        <span className="truncate">{costCenter.name}</span>
                                    </span>
                                    {renderEditRulesIcon()}
                                </>
                            ) : (
                                renderVendorRuleButton()
                            )
                        ) : (
                            <span className="flex overflow-x-auto">
                                <span className="truncate">{costCenter?.name || "-"}</span>
                            </span>
                        )}
                    </div>
                </li>
                {costCenters2.length > 0 && (
                    <li>
                        <span className="title min-w-100">{t("label:cost_center_2")}</span>
                        <div className="flex text truncate min-w-100 items-center justify-end">
                            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && vendor.isApproved ? (
                                costCenter2 ? (
                                    <>
                                        <span className="flex overflow-x-auto">
                                            <span className="truncate">{costCenter2.name}</span>
                                        </span>
                                        {renderEditRulesIcon()}
                                    </>
                                ) : (
                                    renderVendorRuleButton()
                                )
                            ) : (
                                <span className="flex flex-row overflow-x-auto">
                                    <span className="truncate">{costCenter2?.name || "-"}</span>
                                </span>
                            )}
                        </div>
                    </li>
                )}
                {expenseAccounts.length > 0 && (
                    <li>
                        <span className="title min-w-140">{t("label:expense_account")}</span>
                        <div className="flex text truncate min-w-100 items-center justify-end">
                            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && vendor.isApproved ? (
                                expenseAccount ? (
                                    <>
                                        <span className="flex flex-row overflow-x-auto">
                                            <span className="truncate">
                                                {expenseAccount.accountCode} - {expenseAccount.accountName}
                                            </span>
                                        </span>
                                        {renderEditRulesIcon()}
                                    </>
                                ) : (
                                    renderVendorRuleButton()
                                )
                            ) : (
                                <span className="flex flex-row overflow-x-auto">
                                    <span className="truncate">{(expenseAccount && `${expenseAccount.accountCode} - ${expenseAccount.accountName}`) || "-"}</span>
                                </span>
                            )}
                        </div>
                    </li>
                )}
                <li>
                    <span className="title min-w-140">{t("label:payment_flow_information.title")}</span>
                    <div className="flex text truncate min-w-100 items-center justify-end">
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && vendor.isApproved ? (
                            vendor.rule?.expensePaymentFlowInformation ? (
                                <>
                                    {isPaymentFlowInfoDisabled(vendor.rule?.expensePaymentFlowInformation, company) && (
                                        <span className="relative" style={{ bottom: 3 }}>
                                            <Tooltip
                                                getPopupContainer={getTooltipPopupContainer}
                                                title={t("tooltips:vendor.vendor_rule_inactive_due_to_settings")}
                                                overlayStyle={{ width: "200px" }}
                                            >
                                                <ExclamationCircleOutlined className="mr-6" style={{ color: "#ffc069" }} />
                                            </Tooltip>
                                        </span>
                                    )}
                                    <span className="flex flex-row overflow-x-auto">{t(`label:payment_flow_information.${vendor.rule?.expensePaymentFlowInformation}`)}</span>
                                    {renderEditRulesIcon()}
                                </>
                            ) : (
                                renderVendorRuleButton()
                            )
                        ) : (
                            <span className="flex flex-row overflow-x-auto">
                                {vendor.rule?.expensePaymentFlowInformation ? t(`label:payment_flow_information.${vendor.rule?.expensePaymentFlowInformation}`) : "-"}
                            </span>
                        )}
                    </div>
                </li>
                <li>
                    <span className="title min-w-100">{t("label:payment_term_desc")}</span>
                    <div className="flex text truncate min-w-100 items-center justify-end">
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && vendor.isApproved ? (
                            vendor.rule?.paymentTerm ? (
                                <>
                                    <span className="flex flex-row overflow-x-auto">{vendor.rule?.paymentTerm}</span>
                                    {renderEditRulesIcon()}
                                </>
                            ) : (
                                renderVendorRuleButton()
                            )
                        ) : (
                            <span className="flex flex-row overflow-x-auto">{vendor.rule?.paymentTerm || "-"}</span>
                        )}
                    </div>
                </li>
                <li>
                    <span className="title min-w-100">{t("label:vendor_discount.discount")}</span>
                    <div className="flex text truncate min-w-100 items-center justify-end">
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && vendor.isApproved ? (
                            discountRule ? (
                                <>
                                    <span className="flex flex-row overflow-x-auto">{renderDiscountLabel()} </span>
                                    {renderEditRulesIcon()}
                                </>
                            ) : (
                                renderVendorRuleButton()
                            )
                        ) : (
                            <span className="flex flex-row overflow-x-auto">{renderDiscountLabel()}</span>
                        )}
                    </div>
                </li>
            </ul>
        </>
    )
}
